import React, { useState } from 'react';
import {
    Text,
    TouchableOpacity,
    StyleSheet,
    View,
    StyleProp,
    ViewStyle,
    TextStyle,
} from 'react-native';
import type { LucideIcon } from 'lucide-react-native';
import { studentTheme } from '../../theme/tokens';

export type JuicyButtonVariant = 'primary' | 'secondary' | 'accent' | 'danger';
export type JuicyButtonSize = 'large' | 'medium' | 'small';

interface JuicyButtonProps {
    label: string;
    variant?: JuicyButtonVariant;
    size?: JuicyButtonSize;
    icon?: LucideIcon;
    onPress: () => void;
    disabled?: boolean;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    testID?: string;
    accessibilityLabel?: string;
    accessibilityHint?: string;
}

const variantTheme: Record<
    JuicyButtonVariant,
    { face: string; bevel: string; highlight: string; textColor: string }
> = {
    // Apple Green (the hero "GO!" button)
    primary: {
        face: studentTheme.colors.candy.green,
        bevel: studentTheme.colors.candy.greenBevel,
        highlight: studentTheme.colors.candy.greenHighlight,
        textColor: '#FFFFFF',
    },
    // Sunshine Yellow
    secondary: {
        face: studentTheme.colors.candy.yellow,
        bevel: studentTheme.colors.candy.yellowBevel,
        highlight: studentTheme.colors.candy.yellowHighlight,
        textColor: '#FFFFFF',
    },
    // Sky Blue
    accent: {
        face: studentTheme.colors.candy.blue,
        bevel: studentTheme.colors.candy.blueBevel,
        highlight: studentTheme.colors.candy.blueHighlight,
        textColor: '#FFFFFF',
    },
    // Coral Red
    danger: {
        face: studentTheme.colors.candy.red,
        bevel: studentTheme.colors.candy.redBevel,
        highlight: studentTheme.colors.candy.redHighlight,
        textColor: '#FFFFFF',
    },
};

const sizeConfig: Record<
    JuicyButtonSize,
    { minHeight: number; fontSize: number; paddingHorizontal: number; iconSize: number; bevel: number }
> = {
    large: {
        minHeight: 68,
        fontSize: 26,
        paddingHorizontal: 44,
        iconSize: 30,
        bevel: studentTheme.bevel.large,
    },
    medium: {
        minHeight: 56,
        fontSize: 20,
        paddingHorizontal: 32,
        iconSize: 24,
        bevel: studentTheme.bevel.medium,
    },
    small: {
        minHeight: 46,
        fontSize: 16,
        paddingHorizontal: 20,
        iconSize: 18,
        bevel: studentTheme.bevel.small,
    },
};

/**
 * JuicyButton — 3D tactile pill button with physical bottom bevel and squishy press compression.
 * Inspired directly by the "GO!" button in the target reference.
 */
export const JuicyButton: React.FC<JuicyButtonProps> = ({
    label,
    variant = 'primary',
    size = 'large',
    icon: Icon,
    onPress,
    disabled = false,
    style,
    textStyle,
    testID,
    accessibilityLabel,
    accessibilityHint,
}) => {
    const [isPressed, setIsPressed] = useState(false);
    const colors = variantTheme[variant];
    const sConf = sizeConfig[size];

    const currentBevel = isPressed ? Math.max(1, sConf.bevel - 3) : sConf.bevel;
    const translateY = isPressed ? 3 : 0;

    return (
        <TouchableOpacity
            testID={testID}
            accessibilityLabel={accessibilityLabel || label}
            accessibilityRole="button"
            accessibilityHint={accessibilityHint || `Double tap to ${label}`}
            accessibilityState={{ disabled }}
            onPress={onPress}
            onPressIn={() => setIsPressed(true)}
            onPressOut={() => setIsPressed(false)}
            disabled={disabled}
            activeOpacity={0.9}
            style={[
                styles.button,
                {
                    minHeight: sConf.minHeight,
                    paddingHorizontal: sConf.paddingHorizontal,
                    backgroundColor: disabled ? '#94A3B8' : colors.face,
                    borderBottomColor: disabled ? '#64748B' : colors.bevel,
                    borderBottomWidth: currentBevel,
                    transform: [{ translateY }],
                },
                style,
            ]}
        >
            {Icon && (
                <View style={styles.iconContainer}>
                    <Icon size={sConf.iconSize} color={disabled ? '#CBD5E1' : colors.textColor} strokeWidth={3} />
                </View>
            )}
            <Text
                allowFontScaling={true}
                maxFontSizeMultiplier={1.5}
                style={[
                    styles.label,
                    {
                        fontSize: sConf.fontSize,
                        color: disabled ? '#F1F5F9' : colors.textColor,
                    },
                    textStyle,
                ]}
            >
                {label}
            </Text>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: studentTheme.radii.pill,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255, 255, 255, 0.4)',
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255, 255, 255, 0.2)',
        borderRightWidth: 1,
        borderRightColor: 'rgba(255, 255, 255, 0.2)',
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 6,
        elevation: 6,
        gap: 10,
        alignSelf: 'center',
    },
    iconContainer: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    label: {
        fontWeight: '900',
        textAlign: 'center',
        letterSpacing: 1.2,
    },
});
